import React, { useState, useEffect } from 'react';
import { DashboardApi } from '../../../apis/DashboardApi';
import { ChevronDown, XCircle } from 'lucide-react';
import Sidebar from '../../Sidebar/Sidebar';
import Navbar from '../../Navbars/UserNavbar';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

interface FormData {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    contact_id?: string;
    group_id?: string;
    image?: File;
    recipient_id?: string;
}

interface Group {
    id: number;
    name: string;
}

const EditRecipient = () => {
    const { id } = useParams(); // Get recipient ID from URL
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState<FormData>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        contact_id: '',
        group_id: '',
        recipient_id: id
    });

    const [groups, setGroups] = useState<Group[]>([]);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [previewImage, setPreviewImage] = useState<string>('');

    useEffect(() => {
        // Fetch recipient data
        const fetchRecipientData = async () => {
            try {
                const response:any = await DashboardApi.getRecipientDetails(id);
                if (response?.data?.data) {
                    const recipient = response.data.data.recipient;
                    setFormData({
                        first_name: recipient?.first_name || '',
                        last_name: recipient?.last_name || '',
                        email: recipient?.email || '',
                        phone: recipient?.phone || '',
                        contact_id: recipient?.contact?.name || 'None',
                        group_id: recipient?.group?.name || '',
                        recipient_id: id
                    });
                    if (recipient?.image_path) {
                        setPreviewImage(recipient.temporaryUrl || '');
                    }
                }
            } catch (error) {
                console.error('Error fetching recipient:', error);
            }
        };

        // Fetch groups
        DashboardApi.getRecipientGroup().then((response: any) => {
            if (response?.data?.data) {
                setGroups(response.data.data);
            }
        });

        fetchRecipientData();
    }, [id]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      
        const file = e.target.files?.[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setPreviewImage(URL.createObjectURL(file));
        }
    };

    console?.log(formData)

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        const formDataToSend = new FormData();
        
        // Append all form fields to FormData
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (key === 'image' && value instanceof File) {
                    formDataToSend.append(key, value);
                } else if (key !== 'image') {
                    formDataToSend.append(key, value as string);
                }
            }
        });
    
        try {
            // Send the FormData object, not the original formData
            const response: any = await DashboardApi.editRecipient(formDataToSend);
            if (response?.data?.status === 'success') {
                setShowSuccess(true);
                setTimeout(() => {
                    navigate(`/recipients/${response?.data?.data?.id}`);
                }, 2000);
            }
        } catch (error) {
            console.error('Error updating recipient:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-white">
            <div className="flex flex-col md:flex-row">
                <Sidebar />
                <main className="flex-1 p-6 relative md:ml-64">
                    <Navbar />
                    <div className="p-6 max-w-4xl mx-auto">
                        {showSuccess && (
                            <div className="bg-green-50 text-green-600 p-4 rounded-lg mb-6 flex justify-between items-center">
                                <span>Recipient updated successfully</span>
                                <XCircle className="cursor-pointer" onClick={() => setShowSuccess(false)} />
                            </div>
                        )}

                        <div className="flex items-center gap-2 mb-8">
                            <h1 className="text-2xl font-semibold">Recipients</h1>
                            <span className="text-orange-500">/</span>
                            <span className="text-orange-500">Edit Recipient</span>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Image Upload Section */}
                            <div className="flex justify-center">
                                <div className="relative">
                                    <img
                                        src={previewImage || "/images/profile_image.jpg"}
                                        alt="Profile"
                                        className="w-32 h-32 rounded-full object-cover"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById('image-upload')?.click()}
                                        className="bg-orange-500 text-white text-[10px] px-4 py-2 rounded-lg absolute -bottom-4 left-1/2 -translate-x-1/2"
                                    >
                                        Update Image
                                    </button>
                                    <input
                                        id="image-upload"
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>

                            {/* Form Fields */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* First Name */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">First Name</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border rounded-lg"
                                        required
                                    />
                                </div>

                                {/* Last Name */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Last Name</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border rounded-lg"
                                        required
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border rounded-lg"
                                        required
                                    />
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">Phone Number</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        className="w-full p-3 border rounded-lg"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Contact Status and Group */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Status</label>
                                    <div className="flex gap-4">
                                        {['First Contact', 'Second Contact', 'Third Contact','None']?.map(status => (
                                            <label key={status} className="flex items-center gap-2">
                                                <input
                                                    type="radio"
                                                    name="contact_id"
                                                    value={status}
                                                    checked={formData.contact_id === status}
                                                    onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                                                    className="text-blue-600"
                                                />
                                                <span>{status}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Group</label>
                                    <div className="flex gap-4">
                                        {['Family', 'Friends', 'Spouse', 'Staff','Others']?.map(group => (
                                            <label key={group} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="group_id"
                                                    value={group}
                                                    checked={formData.group_id === group}
                                                    onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                                                    className="text-blue-600"
                                                />
                                                <span>{group}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex justify-end gap-4 pt-12">
                                <button 
                                    type="button" 
                                    onClick={() => navigate('/recipients')}
                                    className="px-6 py-2 text-gray-600"
                                >
                                    Cancel
                                </button>
                                <button 
                                    disabled={loading} 
                                    type="submit" 
                                    className="px-6 py-2 bg-orange-500 text-white rounded-lg"
                                >
                                    {loading ? "Updating..." : "Update Recipient"}
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default EditRecipient;